/**
 * Resizes an image file to specified dimensions using object-fit cover behavior.
 * Uses HTML5 Canvas to perform the resize operation.
 *
 * @param file - The image file to resize
 * @param dimensions - Target dimensions: number for square, [width, height] for rectangle
 * @param quality - JPEG quality (0-1, default: 0.8)
 * @returns Promise that resolves to a base64 data URL of the resized image
 */
export async function resizeImage(
  file: File,
  dimensions: number | [number, number] = 128,
  quality: number = 0.8,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("File is not an image"));
      return;
    }

    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Canvas context not available"));
      return;
    }

    img.onload = () => {
      try {
        const [targetWidth, targetHeight] = Array.isArray(dimensions)
          ? dimensions
          : [dimensions, dimensions];

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const { width, height } = img;
        const targetAspectRatio = targetWidth / targetHeight;
        const sourceAspectRatio = width / height;

        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = width;
        let sourceHeight = height;

        // Object-fit cover behavior: crop the source image to match target aspect ratio
        if (sourceAspectRatio > targetAspectRatio) {
          // Source is wider than target, crop the sides
          sourceWidth = height * targetAspectRatio;
          sourceX = (width - sourceWidth) / 2;
        } else {
          // Source is taller than target, crop the top/bottom
          sourceHeight = width / targetAspectRatio;
          sourceY = (height - sourceHeight) / 2;
        }

        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          targetWidth,
          targetHeight,
        );

        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      } catch (error) {
        reject(new Error(`Failed to resize image: ${error}`));
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      } else {
        reject(new Error("Failed to read file"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

const TEN_MB = 10 * 1024 * 1024; // 10MB default size limit

/**
 * Validates if a file is an image and within size limits.
 *
 * @param file - The file to validate
 * @param maxSizeBytes - Maximum file size in bytes (default: 10MB)
 * @returns Object with validation result and error message if invalid
 */
export function validateImageFile(
  file: File,
  maxSizeBytes = TEN_MB, // 10MB default
): { isValid: boolean; error?: string } {
  if (!file.type.startsWith("image/")) {
    return { isValid: false, error: "File must be an image" };
  }

  if (file.size > maxSizeBytes) {
    const maxSizeMB = (maxSizeBytes / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      error: `File size must be less than ${maxSizeMB}MB`,
    };
  }

  const supportedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/svg+xml",
  ];
  if (!supportedTypes.includes(file.type.toLowerCase())) {
    return {
      isValid: false,
      error:
        "Unsupported image format. Please use JPEG, PNG, GIF, WebP, or SVG.",
    };
  }

  return { isValid: true };
}
